import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { devicesUrl, errorText } from '../../pages/SoftwarePolicy/policyEdit';
import { useV2Account } from '../useV2Account';
import PolicyTab from './PolicyTab';
import QueueTab from './QueueTab';
import ResidentsTab from './ResidentsTab';
import SoftwareBlade from './SoftwareBlade';
import {
  SEVERITIES, SYSINFO_SCRIPT, computerFacts, filterRows, programRows, removeBlock, replacement, scopeWords, statusOf,
} from './deviceInfo';
import { queueRemovals } from './driftRemove';
import { SortHeader, useSort } from './sortable';

const list = (v) => (Array.isArray(v) ? v : []);
const labelsText = (d) => Object.entries(d?.labels || {}).sort(([x], [y]) => x.localeCompare(y)).map(([k, v]) => `${k} is ${v}`);
export const TABS = [{ id: 'hardware', label: 'Hardware' }, { id: 'software', label: 'Software' }, { id: 'policy', label: 'Policy' },
  { id: 'residents', label: 'Residents' }, { id: 'queue', label: 'Queue' }];

export const resultUrl = (account, deviceType, deviceId) => `cma/endpointresult/device?${new URLSearchParams({ account, deviceType, script: SYSINFO_SCRIPT, deviceId }).toString()}`;
export const softwareUrl = (account, deviceType, deviceId) => `cma/softwaredevice?${new URLSearchParams({ account, deviceType, deviceId }).toString()}`;

function Facts({ items, label }) {
  return (
    <dl className="v2-facts" aria-label={label}>
      {items.map((f, i) => (
        <div key={`${f.label}-${i}`}><dt>{f.label}</dt><dd>{f.value}</dd></div>
      ))}
    </dl>
  );
}

// The computer: what it is, how old it is and whether it should be replaced. All from the latest result of the system info script.
function InformationTab({ state }) {
  if (state.state === 'loading') return <div className="v2-loading"><span className="v2-spin" role="status" aria-label="Loading" /></div>;
  if (state.state === 'error') return <div className="v2-alert err">{state.error}</div>;
  if (state.state === 'none') return <div className="v2-empty">This machine has not reported its system information yet.</div>;
  const r = state.results || {};
  const rep = replacement(r);
  return (
    <div className="v2-device-info">
      <div>
        <h3 className="v2-h3">Computer</h3>
        <Facts items={computerFacts(r)} label="Computer" />
      </div>
      <div>
        <h3 className="v2-h3">Age and replacement</h3>
        {!rep && <div className="v2-empty">This machine has not reported its age or replacement assessment yet.</div>}
        {rep && (
          <>
            <div><span className={`v2-pill ${rep.verdict.tone}`}>{rep.verdict.label}</span></div>
            {rep.reasons.map((x) => <div className="v2-sub" key={x}>{x}</div>)}
            <Facts items={rep.facts} label="Age and replacement" />
            {rep.findings.length > 0 && (
              <ul className="v2-findings" aria-label="Replacement findings">
                {rep.findings.map((f, i) => (
                  <li key={`${f.area}-${i}`}><span className={`v2-pill ${SEVERITIES[f.severity]?.tone || 'none'}`}>{SEVERITIES[f.severity]?.label || f.severity}</span>{` ${f.area ? `${f.area}: ` : ''}${f.issue}`}</li>
                ))}
              </ul>
            )}
            {rep.notRun.length > 0 && <div className="v2-sub">{`Not checked: ${rep.notRun.join('; ')}`}</div>}
          </>
        )}
      </div>
    </div>
  );
}

// The software installed on the machine, each program with what the policy says about it. Alphabetical until a heading is chosen. A program can
// be removed from its row, after a confirmation: that one exact copy.
function SoftwareTab({ state, machine, account, request, onOpenBlade }) {
  const [typed, setTyped] = useState('');
  const [thirdParty, setThirdParty] = useState(true);       // Microsoft products are left out until it is cleared
  const [removing, setRemoving] = useState(null);       // the row waiting for the go-ahead
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState(null);
  const { sort, toggle, apply } = useSort();
  const rows = useMemo(() => programRows(state.programs).filter((r) => r.status !== 'notinstalled'), [state.programs]);       // what is installed, and what is required but missing
  const shown = useMemo(() => filterRows(rows, { text: typed, thirdParty }), [rows, typed, thirdParty]);
  const ordered = apply(shown, {
    name: { get: (r) => r.shown },
    version: { get: (r) => r.version },
    policy: { get: (r) => r.policy },
    status: { get: (r) => statusOf(r.status).rank },
    available: { get: (r) => r.available },
    scope: { get: (r) => scopeWords(r.scope) },
  });

  const remove = async (row) => {
    setBusy(true);
    setOutcome(null);
    try {
      const answer = await queueRemovals(request, account, 'apply', [{ deviceIds: [machine.deviceId], name: row.name, version: row.version }]);
      setOutcome({ name: row.shown, ...answer });
    } catch (ex) {
      setOutcome({ name: row.shown, error: errorText(ex) });
    } finally {
      setBusy(false);
      setRemoving(null);
    }
  };
  const problems = outcome ? [...list(outcome.refused), ...list(outcome.failed)] : [];
  const queuedCount = outcome ? list(outcome.queued).length : 0;
  const noun = 'removal job';

  return (
    <section aria-label="Software">
      <div className="v2-bar">
        <h2 className="v2-h2">Software Installed on this Device</h2>
        <button type="button" className="v2-btn primary" onClick={onOpenBlade}>Add software</button>
      </div>
      <div className="v2-bar">
        <input className="v2-input" type="search" aria-label="Filter software" placeholder="Filter by name, publisher or version" value={typed} onChange={(e) => setTyped(e.target.value)} style={{ maxWidth: 300 }} />
        <label className="v2-check"><input type="checkbox" checked={thirdParty} onChange={(e) => setThirdParty(e.target.checked)} />Third-Party Only</label>
        <span className="v2-sub" role="status">{state.state === 'ok' ? `${shown.length} of ${rows.length} programs` : ''}</span>
      </div>
      {outcome?.error && <div className="v2-alert err" role="alert">{outcome.error}</div>}
      {outcome && !outcome.error && (
        <div className={`v2-alert${problems.length ? ' info' : ''}`} role="status">
          {`${queuedCount} ${noun}${queuedCount === 1 ? '' : 's'} of ${outcome.name} in the queue.`}
          {list(outcome.queued).map((q) => <div className="v2-sub" key={q.id}>{`${q.name}: ${q.id}`}</div>)}
          {problems.map((p) => <div className="v2-sub" key={`p${p.key}${p.error}`}>{`Not queued, ${p.name}: ${p.error}`}</div>)}
        </div>
      )}
      {state.state === 'loading' && <div className="v2-loading"><span className="v2-spin" role="status" aria-label="Loading" /></div>}
      {state.state === 'error' && <div className="v2-alert err">{state.error}</div>}
      {state.state === 'noresult' && <div className="v2-empty">This machine has not reported its installed software yet.</div>}
      {state.state === 'ok' && (rows.length === 0 ? <div className="v2-empty">No software was reported.</div> : (
        <table className="v2-table" aria-label="Installed software">
          <thead>
            <tr>
              <SortHeader k="name" sort={sort} onSort={toggle}>Application</SortHeader>
              <SortHeader k="version" sort={sort} onSort={toggle}>Version</SortHeader>
              <SortHeader k="policy" sort={sort} onSort={toggle}>Policy</SortHeader>
              <SortHeader k="status" sort={sort} onSort={toggle}>Status</SortHeader>
              <SortHeader k="available" sort={sort} onSort={toggle}>Newest</SortHeader>
              <SortHeader k="scope" sort={sort} onSort={toggle}>Listed</SortHeader>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {ordered.map((r, i) => {
              const why = removeBlock(r);
              return (
                <tr key={`${r.shown}-${r.version}-${r.scope}-${i}`}>
                  <td>
                    <div className="name" data-testid="program-name" style={{ fontWeight: 700 }}>{r.shown}</div>
                    <div className="v2-sub">{[r.publisher, r.reason, r.decidedBy ? `Set by ${r.decidedBy}` : null].filter(Boolean).join(' - ')}</div>
                  </td>
                  <td>{r.installed ? r.version : ''}</td>
                  <td>{r.policy}</td>
                  <td>{r.status !== 'notinpolicy' && <span className={`v2-pill ${statusOf(r.status).tone}`} title={statusOf(r.status).meaning}>{statusOf(r.status).label}</span>}</td>
                  <td>{r.available || ''}</td>
                  <td>{scopeWords(r.scope)}</td>
                  <td className="nowrap">
                    {r.installed && (
                      <button type="button" className="v2-btn" aria-label={`Remove ${r.shown}${r.version ? ` ${r.version}` : ''}`} disabled={busy || Boolean(why)} title={why}
                        onClick={() => setRemoving(r)}>Remove</button>
                    )}
                  </td>
                </tr>
              );
            })}
            {ordered.length === 0 && <tr><td colSpan={7}><div className="v2-empty">No program matches the filters.</div></td></tr>}
          </tbody>
        </table>
      ))}

      {removing && (
        <div className="v2-dialog-host">
          <div className="v2-dialog" role="dialog" aria-label={`Remove ${removing.shown}`}>
            <h2>{`Remove ${removing.shown}?`}</h2>
            <p>{`Version ${removing.version} will be removed from ${machine.hostname}. Data the program keeps elsewhere is not removed.`}</p>
            {removing.scope === 'user' && <p className="v2-sub">This copy is installed for a user, so it is removed in that user&apos;s own session. They have to be signed in at the machine; if they are not, the job&apos;s result says it was not removed.</p>}
            <div className="actions">
              <button type="button" className="v2-btn" onClick={() => setRemoving(null)} disabled={busy}>Cancel</button>
              <button type="button" className="v2-btn primary" onClick={() => remove(removing)} disabled={busy}>Remove</button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

// One machine, V2: what the system info script gathered about it, and the software installed on it with the policy's view of each program.
export default function DevicePage() {
  const jApi = useApi();
  const apiRef = useRef(jApi);
  apiRef.current = jApi;
  const { deviceId } = useParams();
  const [params, setParams] = useSearchParams();
  const { account, error: accountsError } = useV2Account();
  const code = account?.code;
  const [machine, setMachine] = useState({ state: 'loading' });
  const [system, setSystem] = useState({ state: 'loading' });
  const [software, setSoftware] = useState({ state: 'loading', programs: [] });
  const [bladeOpen, setBladeOpen] = useState(false);
  const tab = TABS.some((t) => t.id === params.get('tab')) ? params.get('tab') : TABS[0].id;
  const chooseTab = (id) => { const next = new URLSearchParams(params); next.set('tab', id); setParams(next, { replace: true }); };

  const load = useCallback(async () => {
    if (!code) return;
    setMachine({ state: 'loading' });
    setSystem({ state: 'loading' });
    setSoftware({ state: 'loading', programs: [] });
    let device = null;
    try {
      const response = await apiRef.current.request('GET', devicesUrl(code));
      device = list(response.data?.devices).find((d) => d.deviceId === deviceId) || null;
      setMachine(device ? { state: 'ok', device } : { state: 'missing' });
    } catch (ex) {
      setMachine({ state: 'error', error: errorText(ex) });
    }
    if (!device) { setSystem({ state: 'none' }); setSoftware({ state: 'noresult', programs: [] }); return; }
    const deviceType = device.labels?.devicetype || 'workstation';
    apiRef.current.request('GET', resultUrl(code, deviceType, deviceId))
      .then((r) => setSystem(r.data?.item?.results ? { state: 'ok', results: r.data.item.results } : { state: 'none' }))
      .catch((ex) => setSystem(ex?.response?.status === 404 ? { state: 'none' } : { state: 'error', error: errorText(ex) }));
    apiRef.current.request('GET', softwareUrl(code, deviceType, deviceId))
      .then((r) => setSoftware(r.data?.status === 'noresult' ? { state: 'noresult', programs: [] } : { state: 'ok', programs: list(r.data?.programs) }))
      .catch((ex) => setSoftware({ state: 'error', error: errorText(ex), programs: [] }));
  }, [code, deviceId]);

  useEffect(() => { setBladeOpen(false); load(); }, [load]);

  const device = machine.device;
  const name = device?.hostname || deviceId;
  return (
    <div>
      <div className="v2-head">
        <div>
          <p><Link to={`/v2/devices${code ? `?account=${encodeURIComponent(code)}` : ''}`}>&larr; Devices</Link></p>
          <h1>{name}</h1>
          {device && (
            <div>
              <span className={`v2-pill ${/^connected$/i.test(device.connectionState || '') ? 'ok' : 'none'}`}>{device.connectionState || 'Unknown'}</span>
              {labelsText(device).map((t) => <span className="v2-chip" key={t}>{t}</span>)}
            </div>
          )}
        </div>
      </div>

      {accountsError && <div className="v2-alert err">{accountsError}</div>}
      {machine.state === 'error' && <div className="v2-alert err">{machine.error}</div>}
      {machine.state === 'missing' && <div className="v2-alert info">This machine is not in the account.</div>}

      <div className="v2-tabs" role="tablist" aria-label="Device">
        {TABS.map((t) => (
          <button key={t.id} type="button" role="tab" id={`v2-tab-${t.id}`} aria-selected={tab === t.id} aria-controls={`v2-panel-${t.id}`} className="v2-tab" onClick={() => chooseTab(t.id)}>{t.label}</button>
        ))}
      </div>
      <section className="v2-card" role="tabpanel" id={`v2-panel-${tab}`} aria-labelledby={`v2-tab-${tab}`}>
        {tab === 'hardware' && <div className="v2-pad"><InformationTab state={system} /></div>}
        {tab === 'software' && (
          <SoftwareTab state={software} machine={{ deviceId, hostname: name }} account={code} request={(...a) => apiRef.current.request(...a)} onOpenBlade={() => setBladeOpen(true)} />
        )}
        {tab === 'policy' && (
          <PolicyTab state={software} machine={{ deviceId, hostname: name }} account={code} request={(...a) => apiRef.current.request(...a)} />
        )}
        {tab === 'residents' && device && (
          <ResidentsTab account={code} deviceType={device.labels?.devicetype || 'workstation'} deviceId={deviceId} request={(...a) => apiRef.current.request(...a)} />
        )}
        {tab === 'queue' && device && (
          <QueueTab account={code} deviceType={device.labels?.devicetype || 'workstation'} deviceId={deviceId} request={(...a) => apiRef.current.request(...a)} />
        )}
      </section>

      {bladeOpen && device && <SoftwareBlade account={code} devices={[{ deviceId: device.deviceId, hostname: device.hostname }]} installed={software.programs} onClose={() => setBladeOpen(false)} />}
    </div>
  );
}
